import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, Input, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogBaseComponent } from '@/app/core/components';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { parseDateOnly } from '@/app/shared/utils';

export interface DeviationData {
    id?: string;
    deviationNumber?: string;
    description?: string;
    referenceNote?: string;
    dateOpened?: Date;
    dateClosed?: Date;
    status?: string;
    impact?: string;
    resolution?: string;
    source?: string;
    lotNumber?: string;
    correctiveAction?: string;
    preventiveAction?: string;
}

@Component({
    selector: 'qc-add-deviation-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        Button,
        InputTextModule,
        DropdownModule,
        TextareaModule,
        CalendarModule,
    ],
    template: `
        <div class="w-[700px] max-w-[90vw] bg-white rounded-lg shadow-2xl" style="overflow: visible;">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ isEditMode ? 'Edit Deviation' : 'Add Deviation' }}
                </h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6 max-h-[70vh] overflow-y-auto">
                <form [formGroup]="deviationForm" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Deviation Number *</label>
                            <input
                                pInputText
                                formControlName="deviationNumber"
                                placeholder="e.g., 2019-006"
                                class="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <p-dropdown
                                formControlName="status"
                                [options]="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select status"
                                [showClear]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Source</label>
                            <p-dropdown
                                formControlName="source"
                                [options]="sourceOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select source"
                                [showClear]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Lot Number</label>
                            <input
                                pInputText
                                formControlName="lotNumber"
                                placeholder="e.g., LOT-2024-001"
                                class="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Date Opened</label>
                            <p-calendar
                                formControlName="dateOpened"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>

                        <div class="form-field">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Date Closed</label>
                            <p-calendar
                                formControlName="dateClosed"
                                dateFormat="yy-mm-dd"
                                [showIcon]="true"
                                styleClass="w-full"
                            />
                        </div>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            pTextarea
                            formControlName="description"
                            placeholder="Description of the deviation"
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Reference Note</label>
                        <textarea
                            pTextarea
                            formControlName="referenceNote"
                            placeholder="Reference note for the deviation"
                            rows="2"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                        <textarea
                            pTextarea
                            formControlName="impact"
                            placeholder="Impact assessment"
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Corrective Action</label>
                        <textarea
                            pTextarea
                            formControlName="correctiveAction"
                            placeholder="Corrective action taken"
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Preventive Action</label>
                        <textarea
                            pTextarea
                            formControlName="preventiveAction"
                            placeholder="Preventive action to avoid recurrence"
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>

                    <div class="form-field">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Resolution</label>
                        <textarea
                            pTextarea
                            formControlName="resolution"
                            placeholder="How the deviation was resolved"
                            rows="3"
                            class="w-full"
                        ></textarea>
                    </div>
                </form>
            </div>

            <div class="flex justify-end gap-3 p-6 border-t border-gray-200">
                <p-button label="Cancel" severity="secondary" variant="outlined" (click)="close()" class="mr-2" />
                <p-button
                    [label]="isEditMode ? 'Update' : 'Add'"
                    severity="primary"
                    (click)="save()"
                    [disabled]="!deviationForm.valid"
                />
            </div>
        </div>
    `,
    styles: [
        `
            :host ::ng-deep .p-calendar {
                z-index: 9999;
            }
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
                top: auto !important;
                left: auto !important;
            }
        `,
    ],
})
export class AddDeviationDialogComponent extends DialogBaseComponent implements OnInit {
    @Input() initialData?: DeviationData;
    @Output() deviationAdded = new EventEmitter<DeviationData>();

    private fb = inject(FormBuilder);

    deviationForm!: FormGroup;

    statusOptions = [
        { label: 'Open', value: 'Open' },
        { label: 'In Progress', value: 'In Progress' },
        { label: 'Under Review', value: 'Under Review' },
        { label: 'Closed', value: 'Closed' },
        { label: 'Cancelled', value: 'Cancelled' },
    ];

    sourceOptions = [
        { label: 'Internal', value: 'Internal' },
        { label: 'Fabricator', value: 'Fabricator' },
        { label: 'Test Lab (OOS)', value: 'TestLab' },
    ];

    get isEditMode(): boolean {
        return !!this.initialData?.id;
    }

    constructor() {
        super();
    }

    ngOnInit() {
        this.initializeForm();
        if (this.initialData) {
            this.patchFormWithInitialData();
        }
    }

    private initializeForm() {
        this.deviationForm = this.fb.group({
            deviationNumber: ['', [Validators.required]],
            description: [''],
            referenceNote: [''],
            dateOpened: [''],
            dateClosed: [''],
            status: [''],
            impact: [''],
            resolution: [''],
            source: ['Internal'],
            lotNumber: [''],
            correctiveAction: [''],
            preventiveAction: [''],
        });
    }

    private patchFormWithInitialData() {
        if (this.initialData) {
            this.deviationForm.patchValue({
                ...this.initialData,
                dateOpened: parseDateOnly(this.initialData.dateOpened),
                dateClosed: parseDateOnly(this.initialData.dateClosed),
            });
        }
    }

    save() {
        if (this.deviationForm.valid) {
            const formValue = this.deviationForm.value;
            const deviationData: DeviationData = {
                id: this.initialData?.id,
                ...formValue,
            };
            this.deviationAdded.emit(deviationData);
            this.close(true);
        }
    }
}
